import { ContextMenu, ContextMenuOption } from '@infotech-soe/erp-ui-kit';
import React, { useCallback, useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';

import {
  Caption as CaptionModel,
  CaptionType,
} from '../../../../models/caption/Caption';
import {
  DatasetConfig,
  TToolbarConfig,
} from '../../../../models/user-config/DisplayModeConfig';
import CommandViewModel, {
  CommandListEntryType,
  CommandListEntryViewModel,
  CommandName,
} from '../../../../services/commands/CommandViewModel';
import { useAppDispatch } from '../../../../store/hooks';
import categoryActions from '../../../../store/slices/category';
import dataset from '../../../../store/slices/dataset';
import { ContextMenu as TContextMenu } from '../../../hooks/useContextMenu';
import { getCaptionText } from '../../Caption';
import { DatasetGridContainerProps } from '../DatasetGridContainer';

interface Props
  extends
    Pick<DatasetGridContainerProps, 'fieldsConditionsCount'>,
    Required<Pick<DatasetGridContainerProps, 'commands'>> {
  config: DatasetConfig;
  categoryId: string;
  datasetName: string;
  datasetId: string;
  contextMenu: TContextMenu;
  onInitiateCommand: (name: string) => void;
  onSetIsPopoverOpen: (open: boolean) => void;
  gridFocus: () => void;
  onExportCSV: () => void;
  onExportExcel: () => void;
  onCopy: () => Promise<void>;
  conditionsCount: number | string;
}

type BadgeItems = Record<
  CommandListEntryViewModel['name'],
  string | number | undefined
>;

const commandsFactory = (
  commands: CommandViewModel[],
  badges: BadgeItems,
  formatMessage: IntlFormatters['formatMessage'],
  defaultCommand?: CommandName,
  toolbarCommands?: Record<CommandName, TToolbarConfig>
): Array<ContextMenuOption<string>> => [
  ...commands
    .map<ContextMenuOption | undefined>(command => {
      if (
        command.type === CommandListEntryType.COMMAND &&
        !(
          toolbarCommands?.[command.name]?.hideInMenu ??
          toolbarCommands?.[command.name]?.hide
        )
      ) {
        const caption: CaptionModel = command.menuLabel
          ? { type: CaptionType.EXTERNAL, caption: command.menuLabel }
          : command.caption;
        const labelText = getCaptionText(caption, formatMessage);
        return {
          type: 'listItem',
          badge: command.badge ?? badges?.[command.name],
          disabled: !command.available,
          hotKey: command.hotKey,
          icon: command.icon,
          label:
            defaultCommand === command.name ? (
              <span className="text-bold">{labelText}</span>
            ) : (
              labelText
            ),
          labelText,
          value: command.name,
        };
      } else if (
        command.type === CommandListEntryType.GROUP &&
        command.children.length > 0 &&
        command.available &&
        command.children
          .filter(
            item =>
              !(
                item.type === CommandListEntryType.COMMAND &&
                (toolbarCommands?.[item.name]?.hideInMenu ??
                  toolbarCommands?.[item.name]?.hide)
              )
          )
          .some(item => item.type !== CommandListEntryType.SEPARATOR)
      ) {
        return {
          type: 'listItem',
          icon: command.icon,
          label: getCaptionText(command.caption, formatMessage),
          children: undefined,
          options: commandsFactory(
            command.children,
            badges,
            formatMessage,
            defaultCommand,
            toolbarCommands
          ).filter(
            (item, index, arr) =>
              !(
                item?.type === CommandListEntryType.SEPARATOR &&
                (index === 0 ||
                  index === arr.length - 1 ||
                  arr[index - 1]?.type === CommandListEntryType.SEPARATOR)
              )
          ),
          value: command.name,
        };
      } else if (command.type === CommandListEntryType.SEPARATOR) {
        return command;
      }
    })
    .filter(item => item !== undefined)
    // delete double separators
    .filter(
      (item, index, arr) =>
        !(
          item?.type === CommandListEntryType.SEPARATOR &&
          index > 0 &&
          arr[index - 1]?.type === CommandListEntryType.SEPARATOR
        )
    )
    // delete start | end separators
    .filter(
      (item, index, arr) =>
        !(
          item?.type === CommandListEntryType.SEPARATOR &&
          (index === 0 || index === arr.length - 1)
        )
    )
    .filter((item): item is ContextMenuOption<string> => !!item),
];

const GridContextMenu = ({
  categoryId,
  commands,
  conditionsCount,
  config,
  contextMenu,
  datasetId,
  datasetName,
  fieldsConditionsCount,
  gridFocus,
  onCopy,
  onExportCSV,
  onExportExcel,
  onInitiateCommand,
  onSetIsPopoverOpen,
}: Props): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { defaultCommand } = config.preset[config.currentPreset];

  const contextMenuOptions = useMemo(
    () =>
      commandsFactory(
        commands,
        { filter: conditionsCount, filterByColumn: fieldsConditionsCount },
        formatMessage,
        defaultCommand,
        config.preset[config.currentPreset].toolbarCommands
      ),
    [
      commands,
      conditionsCount,
      config.currentPreset,
      config.preset,
      defaultCommand,
      fieldsConditionsCount,
      formatMessage,
    ]
  );

  const onContextMenuClick = useCallback(
    (item: string) => {
      switch (item) {
        case 'filterByColumn': {
          onSetIsPopoverOpen(true);
          break;
        }
        case 'clearColumnsFilters': {
          dispatch(
            categoryActions.updateFieldsConditions({
              categoryId,
              datasetName,
              fieldsConditions: {},
            })
          );
          dispatch(dataset.update({ datasetId, forceDOMFocus: true }));
          break;
        }
        case 'copy': {
          void onCopy();
          break;
        }
        case 'excelExport': {
          onExportExcel();
          break;
        }
        case 'csvExport': {
          onExportCSV();
          break;
        }
        default: {
          onInitiateCommand(item);
        }
      }
      contextMenu.close();
    },
    [
      categoryId,
      datasetName,
      contextMenu,
      datasetId,
      onCopy,
      onInitiateCommand,
      onSetIsPopoverOpen,
      onExportCSV,
      onExportExcel,
      dispatch,
    ]
  );

  const onContextMenuClose = useCallback(() => {
    contextMenu.close();
    gridFocus();
  }, [contextMenu, gridFocus]);

  return (
    <ContextMenu
      x={contextMenu.positionX}
      y={contextMenu.positionY}
      open={contextMenu.isOpen}
      onOpen={onContextMenuClose}
      onClick={onContextMenuClick}
      options={contextMenuOptions}
    />
  );
};

export default GridContextMenu;
